/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  CardContent,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { aiScheduleApi } from '../../services/api';
import { useAiDateTimeFormatting } from '../../hooks/useAiDateTimeFormatting';
import { PageHeader, LoadingSpinner, ErrorAlert, ConfirmDialog } from '../common';
import { ScheduleForm } from './ScheduleForm';
import type { AISchedule } from '../../types';

const ScheduleList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const { formatDateTime } = useAiDateTimeFormatting();
  const [formOpen, setFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AISchedule | undefined>();
  const [deletingSchedule, setDeletingSchedule] = useState<AISchedule | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['schedules'],
    queryFn: () => aiScheduleApi.list({}),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => aiScheduleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setDeletingSchedule(null);
    },
    onError: (error: any) => {
      console.error('Failed to delete schedule:', error);
      
      // If it's a 404, the schedule was already deleted - refresh the list
      if (error.response?.status === 404) {
        console.log('Schedule already deleted, refreshing list...');
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
      }
      
      setDeletingSchedule(null);
    },
  });

  // Toggle active/inactive mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      aiScheduleApi.update(id, { is_active }),
    onMutate: async ({ id, is_active }) => {
      await queryClient.cancelQueries({ queryKey: ['schedules'] });
      const previous = queryClient.getQueryData(['schedules']);
      queryClient.setQueryData(['schedules'], (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((s: AISchedule) =>
            s.id === id ? { ...s, is_active } : s
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['schedules'], context.previous);
      }
      setTogglingId(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setTogglingId(null);
    },
  });

  const handleToggleActive = (schedule: AISchedule) => {
    setTogglingId(schedule.id);
    toggleActiveMutation.mutate({
      id: schedule.id,
      is_active: !schedule.is_active,
    });
  };

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  return (
    <Box>
      <PageHeader
        title="Schedules"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingSchedule(undefined);
              setFormOpen(true);
            }}
          >
            Add Schedule
          </Button>
        }
      />

      <ScheduleForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingSchedule(undefined);
        }}
        schedule={editingSchedule}
      />

      <ConfirmDialog
        open={!!deletingSchedule}
        title="Delete Schedule"
        message={`Are you sure you want to delete this schedule for "${deletingSchedule?.system_name}"? This action cannot be undone.`}
        onConfirm={() => {
          if (deletingSchedule) {
            deleteMutation.mutate(deletingSchedule.id);
          }
        }}
        onCancel={() => setDeletingSchedule(null)}
      />

      {isLoading ? (
        <LoadingSpinner message="Loading schedules..." />
      ) : isMobile ? (
        // Mobile Card View
        <Grid container spacing={2}>
          {data?.results.map((schedule) => (
            <Grid size={{ xs: 12 }} key={schedule.id}>
              <Card>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="medium"
                        sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}
                      >
                        {schedule.system_name}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        <Chip
                          label={schedule.type_display}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={schedule.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={schedule.is_active ? 'success' : 'default'}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {schedule.type === 'RECURRING' && schedule.interval_every && schedule.interval_period
                      ? `Every ${schedule.interval_every} ${schedule.interval_period}` 
                      : schedule.start_at ? formatDateTime(schedule.start_at, undefined, '-') : '-'}
                  </Typography>
                  <Box display="flex" justifyContent="flex-end" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(schedule)}
                      color={schedule.is_active ? 'warning' : 'success'}
                      disabled={togglingId === schedule.id}
                    >
                      {togglingId === schedule.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        schedule.is_active ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        const fullSchedule = await aiScheduleApi.get(schedule.id);
                        setEditingSchedule(fullSchedule);
                        setFormOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeletingSchedule(schedule)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {data?.results.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No schedules found.</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>System</TableCell>
                <TableCell>Schedule Type</TableCell>
                <TableCell>Recurrence</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.results.map((schedule) => (
                <TableRow key={schedule.id} hover>
                  <TableCell>{schedule.system_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={schedule.type_display}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {schedule.type === 'RECURRING' && schedule.interval_every && schedule.interval_period
                      ? `Every ${schedule.interval_every} ${schedule.interval_period}` 
                      : schedule.start_at ? formatDateTime(schedule.start_at, undefined, '-') : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={schedule.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={schedule.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(schedule.created_at, undefined, '-')}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(schedule)}
                      color={schedule.is_active ? 'warning' : 'success'}
                      disabled={togglingId === schedule.id}
                    >
                      {togglingId === schedule.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        schedule.is_active ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        const fullSchedule = await aiScheduleApi.get(schedule.id);
                        setEditingSchedule(fullSchedule);
                        setFormOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeletingSchedule(schedule)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {data?.results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No schedules found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ScheduleList;
